<template>
  <div
    class="relative mt-4 overflow-hidden rounded-lg px-6 py-4 text-md shadow-lg-cool-grey"
    data-cy="alert"
    :class="[widthAuto ? 'inline-flex' : 'flex', styles.container]"
  >
    <div class="flex grow items-baseline" :class="styles.text">
      <i class="fas mr-4" :class="styles.icon" />
      <div>
        <slot></slot>
      </div>
    </div>
    <div
      v-if="dismissible && id"
      class="absolute top-2 right-4 cursor-pointer"
      :class="styles.text"
      @click="dismiss"
    >
      <i class="fas fa-times"></i>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted } from 'vue';
import { AlertType } from '@/store/ui-store';

const MILLISECONDS_IN_SECOND = 1000;

const ACCENT =
  'font-semibold after:absolute after:bottom-0 after:left-0 after:top-0 after:w-1.5';

const VARIANT_STYLES: Record<
  AlertType,
  { container: string; text: string; icon: string }
> = {
  [AlertType.DANGER]: {
    container: `after:w-1.50 bg-danger-50 ${ACCENT} after:bg-danger-700`,
    text: 'text-danger-900',
    icon: 'fa-times-circle text-danger-700',
  },
  [AlertType.SUCCESS]: {
    container: `after:w-1.50 bg-success-50 ${ACCENT} after:bg-success-700`,
    text: 'text-success-900',
    icon: 'fa-check-circle text-success-700',
  },
  [AlertType.WARNING]: {
    container: `bg-warning-100 ${ACCENT} after:bg-warning-700`,
    text: 'text-warning-900',
    icon: 'fa-exclamation-triangle text-warning-700',
  },
  [AlertType.INFO]: {
    container: `after:w-1.50 bg-info-50 ${ACCENT} after:bg-info-700`,
    text: 'text-blue-900',
    icon: 'fa-info-circle text-info-700',
  },
};

const props = withDefaults(
  defineProps<{
    variant?: AlertType;
    id?: number | null;
    dismissible?: boolean;
    duration?: number;
    widthAuto?: boolean;
  }>(),
  {
    variant: AlertType.DANGER,
    id: null,
    dismissible: false,
    duration: 10,
    widthAuto: false,
  },
);

const emit = defineEmits(['dismissed']);

const styles = computed(() => VARIANT_STYLES[props.variant]);

const dismissTimeout = setTimeout(
  dismiss,
  props.duration * MILLISECONDS_IN_SECOND,
);

onUnmounted(() => clearTimeout(dismissTimeout));

function dismiss() {
  emit('dismissed');
}
</script>
